import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, UserPlus, Users, UserCheck, UserX, Search } from 'lucide-react';
import { toast } from 'sonner';

const Friends = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFriends();
    fetchRequests();
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends`);
      setFriends(response.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/friends/requests`);
      setRequests(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const searchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search`, {
        params: { q: searchQuery }
      });
      setSearchResults(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const sendFriendRequest = async (username) => {
    try {
      await axios.post(`${API}/friends/request`, {
        to_username: username
      });
      toast.success('Friend request sent!');
      setSearchQuery('');
      setSearchResults([]);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to send request');
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/accept`);
      toast.success('Friend request accepted!');
      fetchFriends();
      fetchRequests();
    } catch (e) {
      console.error(e);
      toast.error('Failed to accept request');
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/reject`);
      toast.success('Friend request rejected');
      fetchRequests();
    } catch (e) {
      console.error(e);
      toast.error('Failed to reject request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mr-4"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Friends
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Search */}
        <Card className="glass p-6 border border-slate-700 mb-8 fade-in">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-purple-400" />
            Add Friends
          </h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
              data-testid="search-user-input"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2">
              {searchResults.map(result => (
                <div
                  key={result.id}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                  data-testid={`search-result-${result.id}`}
                >
                  <div>
                    <p className="text-white font-semibold">{result.username}</p>
                    <p className="text-sm text-slate-400">{result.email}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => sendFriendRequest(result.username)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid={`send-request-btn-${result.id}`}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Friend Requests */}
        {requests.length > 0 && (
          <Card className="glass p-6 border border-slate-700 mb-8 fade-in">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-purple-400" />
              Friend Requests
              <Badge className="bg-purple-500/20 text-purple-400">{requests.length}</Badge>
            </h3>
            <div className="space-y-3">
              {requests.map(request => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                  data-testid={`friend-request-${request.id}`}
                >
                  <div>
                    <p className="text-white font-semibold">{request.from_user?.username}</p>
                    <p className="text-sm text-slate-400">{request.from_user?.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => acceptRequest(request.id)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`accept-request-btn-${request.id}`}
                    >
                      <UserCheck className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rejectRequest(request.id)}
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                      data-testid={`reject-request-btn-${request.id}`}
                    >
                      <UserX className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Friends List */}
        <Card className="glass p-6 border border-slate-700 fade-in">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            My Friends
            <Badge className="bg-purple-500/20 text-purple-400">{friends.length}</Badge>
          </h3>
          
          {friends.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No friends yet. Start adding friends to tag them when you check into bars!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {friends.map(friend => (
                <div
                  key={friend.id}
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                  data-testid={`friend-item-${friend.id}`}
                >
                  <div className="flex items-center gap-3">
                    {friend.profile_picture_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${friend.profile_picture_url}`}
                        alt={friend.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">{friend.username[0].toUpperCase()}</span>
                      </div>
                    )}
                    <div>
                      <p className="text-white font-semibold">{friend.username}</p>
                      <p className="text-sm text-slate-400">{friend.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {friend.age && (
                      <Badge className="bg-purple-500/20 text-purple-400">{friend.age} yo</Badge>
                    )}
                    <Badge className="bg-slate-700 text-slate-300">{friend.gender}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Friends;